import { FC, useContext, useState } from 'react';
import {
  CombinedTableModel,
  DataGridTable,
  ListContainer,
  ListContext,
  ListLayout,
  Loader,
} from '../../ui';
import { useSignupRequest } from '../hooks';
import { getSignupRequests } from '../network';
import {
  GridCheckCircleIcon,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { formatNullValues } from '../../ui/components/DataGridPro/helpers';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import styled from '@emotion/styled';
import { QRCode } from './QRCode';
import { observer } from 'mobx-react-lite';
import { useCoordinatorStore } from '../../coordinator/coordinatorStore';
import { enqueueSnackbar } from 'notistack';
import { ActionsIconButton } from './styles';
import { Cancel, Pending } from '@mui/icons-material';
import { StatusChip } from '../../scan/ui/ScanStatusChips';

export const SignupRequestsTable: FC = () => {
  return (
    <div>
      <ListContainer dataRequest={getSignupRequests}>
        <ListLayout>
          <SignupDataGrid />
        </ListLayout>
      </ListContainer>
    </div>
  );
};

const SignupDataGrid: FC = () => {
  const { data, loading, reload, filters, setFilters } =
    useContext(ListContext);

  const columns: GridColDef<CombinedTableModel>[] = [
    {
      field: 'first_name',
      headerName: 'First Name',
      flex: 1,
    },
    {
      field: 'last_name',
      headerName: 'Last Name',
      flex: 1,
      valueGetter: (params) => {
        return formatNullValues(params.value);
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
    },
    {
      field: 'date_joined',
      flex: 1,
      headerName: 'Joined',
      valueGetter: (params) => {
        return new Date(params.value).toLocaleDateString();
      },
    },
    {
      field: 'signup_status',
      flex: 1,
      headerName: 'Application Status',
      renderCell: (params) => {
        return (
          <StatusChip
            label={params.value === 'rejected' ? 'Rejected' : 'Pending'}
            icon={params.value === 'rejected' ? <Cancel /> : <Pending />}
            color={params.value === 'rejected' ? 'error' : 'warning'}
          />
        );
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        return <ActionsCell params={params} reload={reload} />;
      },
    },
  ];

  const showRejectedRequests = () => {
    setFilters({ ...filters, signup_status: 'rejected' });
  };

  const hideRejectedRequests = () => {
    setFilters({ ...filters, signup_status: undefined });
  };

  const isShowingRejected = filters?.signup_status === 'rejected';

  return (
    <div>
      <Header>
        <HeaderTitleContainer>
          <Typography variant='h2'>Pending Patient Sign-Ups</Typography>
          <QRCode />
        </HeaderTitleContainer>
        {isShowingRejected ? (
          <Link onClick={hideRejectedRequests} underline='always'>
            Hide Rejected Sign-Ups
          </Link>
        ) : (
          <Link onClick={showRejectedRequests} underline='always'>
            Show Rejected Sign-Ups
          </Link>
        )}
      </Header>

      {data?.length === 0 ? (
        <WidgetCard>
          No {isShowingRejected ? 'rejected' : 'pending'} sign-ups...
        </WidgetCard>
      ) : (
        <DataGridTable
          loading={loading}
          data={data}
          columns={columns}
          tableType='signup'
          onRowClick={() => {}}
        />
      )}
    </div>
  );
};

const WidgetCard = styled('div')`
  background: white;
  padding: 2rem;
  border-radius: 1rem;
  margin-top: 0rem;
`;

const Header = styled('div')`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const HeaderTitleContainer = styled('div')`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

interface GridIconButtonProps {
  toolTip: string;
  icon: JSX.Element;
  disabled?: boolean;
  onClick: () => void;
}

const GridIconButton: FC<GridIconButtonProps> = ({
  toolTip,
  onClick,
  icon,
  disabled,
}) => {
  return (
    <Tooltip title={toolTip}>
      <ActionsIconButton onClick={onClick} disabled={disabled}>
        {icon}
      </ActionsIconButton>
    </Tooltip>
  );
};

interface ActionsCellProps {
  params: GridRenderCellParams;
  reload: () => void;
}

const ActionsCell: FC<ActionsCellProps> = observer(({ params, reload }) => {
  const coordinatorStore = useCoordinatorStore();
  const coordinators = coordinatorStore.coordinatorList;
  const { isLoading, acceptPatient, rejectPatient } = useSignupRequest({
    reload,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [selectedCoordinator, setSelectedCoordinator] = useState<string>('');
  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    const id = params.row.id;
    const coordinatorId = selectedCoordinator;
    const hasSelectedCoordinator = Boolean(coordinatorId);
    if (!hasSelectedCoordinator) {
      enqueueSnackbar('Please select a coordinator', { variant: 'error' });
      return;
    }
    await acceptPatient({ userId: id, coordinatorId });
    setIsEditing(false);
    reload();
  };

  const handleCancel = () => {
    setSelectedCoordinator('');
    setIsEditing(false);
  };

  const onChange = (e: SelectChangeEvent) => {
    setSelectedCoordinator(e.target.value);
  };

  const handleDecline = async () => {
    await rejectPatient(params.row.id);
    reload();
  };

  return (
    <>
      <GridIconButton
        toolTip='Accept'
        onClick={handleEdit}
        icon={<GridCheckCircleIcon />}
      />
      {params.row.signup_status !== 'rejected' && (
        <GridIconButton
          toolTip='Decline'
          onClick={handleDecline}
          icon={<CancelIcon />}
        />
      )}
      <Dialog open={isEditing}>
        <DialogTitle>
          Assign a coordinator to {params.row.first_name} {params.row.last_name}
        </DialogTitle>
        <DialogContent>
          <Select fullWidth value={selectedCoordinator} onChange={onChange}>
            {coordinators.map((coordinator) => (
              <MenuItem key={coordinator.id} value={coordinator.id}>
                {coordinator.first_name} {coordinator.last_name}
              </MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          {isLoading ? (
            <Loader size={20} />
          ) : (
            <>
              <Button
                disabled={Boolean(selectedCoordinator) === false}
                onClick={handleSave}
                variant='contained'
              >
                Save
              </Button>
              <Button onClick={handleCancel} variant='containedLight'>
                Cancel
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
});
